import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Terms of Service
          </h1>
          <p className="text-xl text-gray-200">
            Last Updated: February 2026
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-none shadow-xl">
            <CardContent className="p-12">
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6">Agreement to Terms</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  By accessing or using the services of Omnia Paratus Talent Solutions ("Company," "we," "our," or "us"), 
                  you agree to be bound by these Terms of Service. If you disagree with any part of these terms, 
                  you may not access our services.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Services Description</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Omnia Paratus Talent Solutions provides professional recruiting and talent acquisition services, 
                  including but not limited to:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Contingent direct hire recruiting</li>
                  <li>Talent pipeline development</li>
                  <li>Candidate sourcing and screening</li>
                  <li>Recruitment consultation</li>
                </ul>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">User Responsibilities</h2>
                <h3 className="text-2xl font-semibold text-[#0A1F44] mb-4 mt-8">For Employers</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  When engaging our services as an employer, you agree to:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Provide accurate and complete job descriptions and requirements</li>
                  <li>Comply with all applicable employment laws and regulations</li>
                  <li>Maintain confidentiality of candidate information</li>
                  <li>Make hiring decisions free from discrimination</li>
                  <li>Pay agreed-upon fees for successful placements</li>
                </ul>

                <h3 className="text-2xl font-semibold text-[#0A1F44] mb-4 mt-8">For Job Seekers</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  When using our services as a candidate, you agree to:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Provide accurate and truthful information in your application</li>
                  <li>Update us promptly of any changes to your employment status</li>
                  <li>Maintain professionalism in all interactions</li>
                  <li>Not misrepresent your qualifications or experience</li>
                </ul>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Fees and Payment</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Our recruiting services operate on a contingent fee basis. Fees are typically calculated as a 
                  percentage (15-25%) of the candidate's first-year salary and are due upon successful placement. 
                  Specific fee arrangements will be detailed in individual service agreements.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Placement Guarantee</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  We offer a 90-day placement guarantee. If a placed candidate leaves voluntarily or is terminated 
                  for cause within 90 days of their start date, we will provide a replacement candidate at no 
                  additional fee, subject to the terms of our service agreement.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Intellectual Property</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  All content on our website, including text, graphics, logos, and software, is the property of 
                  Omnia Paratus Talent Solutions and is protected by copyright and trademark laws. You may not 
                  reproduce, distribute, or create derivative works without our express written permission.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Confidentiality</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Both parties agree to maintain the confidentiality of all proprietary and sensitive information 
                  shared during the course of our business relationship. This includes but is not limited to 
                  candidate information, job descriptions, and compensation details.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Limitation of Liability</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  To the fullest extent permitted by law, Omnia Paratus Talent Solutions shall not be liable for 
                  any indirect, incidental, special, consequential, or punitive damages arising from your use of 
                  our services. Our total liability shall not exceed the fees paid for the specific service 
                  giving rise to the claim.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Termination</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Either party may terminate our service relationship at any time with written notice. Termination 
                  does not relieve either party of obligations incurred prior to termination, including payment 
                  for placements made before termination.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Governing Law</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  These Terms of Service shall be governed by and construed in accordance with the laws of the 
                  Commonwealth of Massachusetts, without regard to its conflict of law provisions.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Changes to Terms</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  We reserve the right to modify these terms at any time. We will notify users of any material 
                  changes by posting the new Terms of Service on this page and updating the "Last Updated" date.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Contact Information</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <p className="text-gray-700 mb-2">
                    <strong>Email:</strong> contact@omniaparatustalent.io
                  </p>
                  <p className="text-gray-700 mb-2">
                    <strong>Phone:</strong> (978) 340-6952
                  </p>
                  <p className="text-gray-700">
                    <strong>Location:</strong> Boston, MA
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
